import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { HUD } from '@/config/hud';
import { settings } from '@/config/settings';
import { t } from '@/i18n';
import { UI_FONT } from './widgets';

const E = HUD.energy;
const D = HUD.dash;
const DASH_Y = HUD.rows.dash - HUD.rows.energy;

function barLabel(): Text {
  const text = new Text({
    text: '',
    style: new TextStyle({ fontFamily: UI_FONT, fontSize: 12, fill: 0xffffff, letterSpacing: 2 }),
  });
  text.anchor.set(0, 0.5);
  return text;
}

/**
 * Supershot energy bar (labelled, pulses SUPER READY when full) + dash
 * recharge bar underneath (docs/06). Fills are pre-drawn white bars that
 * only change scale/tint per frame — no per-frame Graphics rebuilds.
 */
export class ChargeBars {
  readonly view = new Container();
  private readonly energyGlow = new Graphics();
  private readonly energyFill = new Graphics();
  private readonly dashFill = new Graphics();
  private readonly superLabel = barLabel();
  private readonly dashLabel = barLabel();
  private time = 0;
  private full = false;
  private dashReady = false;
  private lang = '';

  constructor() {
    const bg = new Graphics();
    bg.roundRect(0, 0, E.w, E.h, E.h / 2).fill(0x221c30)
      .stroke({ color: COLORS.outline, width: 3 });
    bg.roundRect(0, DASH_Y, D.w, D.h, D.h / 2).fill(0x221c30)
      .stroke({ color: COLORS.outline, width: 2 });
    this.energyGlow.roundRect(-3, -3, E.w + 6, E.h + 6, E.h / 2 + 3)
      .stroke({ color: COLORS.hudSupershot, width: 2 });
    this.energyGlow.visible = false;
    this.energyFill.roundRect(0, 0, E.w - 4, E.h - 4, (E.h - 4) / 2).fill(0xffffff);
    this.energyFill.position.set(2, 2);
    this.dashFill.roundRect(0, 0, D.w - 2, D.h - 2, (D.h - 2) / 2).fill(0xffffff);
    this.dashFill.position.set(1, DASH_Y + 1);
    this.superLabel.position.set(E.w + E.labelGap, E.h / 2);
    this.dashLabel.position.set(D.w + E.labelGap, DASH_Y + D.h / 2);
    this.view.addChild(
      bg, this.energyGlow, this.energyFill, this.dashFill, this.superLabel, this.dashLabel,
    );
  }

  /** Per frame: energy 0..1, dash recharge 0..1 (1 = ready). */
  set(energyPct: number, dashPct: number): void {
    const full = energyPct >= 1;
    const dashReady = dashPct >= 1;
    if (full !== this.full || dashReady !== this.dashReady || this.lang !== settings.language) {
      this.full = full;
      this.dashReady = dashReady;
      this.lang = settings.language;
      this.superLabel.text = full ? t('hud.superReady') : t('hud.super');
      this.superLabel.style.fill = full ? COLORS.hudSupershot : E.labelDim;
      this.dashLabel.text = t('hud.dash');
      this.dashLabel.style.fill = dashReady ? D.readyColor : D.labelDim;
      this.energyGlow.visible = full;
      this.energyFill.tint = full ? COLORS.hudSupershot : E.chargeColor;
      this.dashFill.tint = dashReady ? D.readyColor : D.chargeColor;
      if (!full) this.energyFill.alpha = this.superLabel.alpha = this.energyGlow.alpha = 1;
    }
    this.energyFill.visible = energyPct > 0;
    this.energyFill.scale.x = Math.max(0.05, Math.min(1, energyPct));
    this.dashFill.scale.x = Math.max(0.04, Math.min(1, dashPct));
  }

  update(dt: number): void {
    this.time += dt;
    if (!this.full) return;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * E.pulseSpeed);
    const a = E.pulseMinAlpha + (1 - E.pulseMinAlpha) * pulse;
    this.superLabel.alpha = a;
    this.energyGlow.alpha = a;
    this.energyFill.alpha = 0.8 + 0.2 * pulse;
  }
}
